// Shared folder-drop walker. Converts a DataTransfer from a drop event into a
// flat File[] array, descending into folders via the webkitGetAsEntry API when
// available, and falling back to dataTransfer.files for single-file legacy
// drops (Safari + kill-switch path).
//
// Kill switches (per surface, so ops can disable independently):
//   • window.qm_disable_global_drop_walk — App.jsx window-level drop (Dashboard).
//   • window.qm_disable_folder_drop_walk — FileImportView drop zone (Stage 1).
// Pass the relevant flag name via `killSwitchKey` so the caller controls which
// window flag gates its own surface.

// Minimal structural shape of the non-standard webkit drag-drop entry tree —
// typed to exactly what this walker reads (honest typing precedent).
interface FsEntryReader {
    readEntries(
        onSuccess: (entries: FsEntry[]) => void,
        onError: () => void,
    ): void;
}
interface FsEntry {
    isFile?: boolean;
    isDirectory?: boolean;
    file?(onSuccess: (file: File) => void, onError: () => void): void;
    createReader?(): FsEntryReader;
}

interface DropSnapshot {
    entries: FsEntry[];
    fallbackFiles: File[];
    killSwitch: boolean;
}

interface SnapshotDropOptions {
    killSwitchKey?: string;
}

// FE-SUBFOLDER-1 (2026-06-13): canonical drop-walk depth cap. Kept in lockstep
// with DEFAULT_SCAN_DEPTH in src/services/file-explorer-intake.js so the SAME
// folder yields the SAME files whether it was loaded via the folder picker or a
// drag-drop. Previously this walker was fully recursive (no cap) while the
// picker capped at 5, so the two surfaces disagreed on deeply-nested files. A
// drop is an explicit, usually-shallow gesture; when the cap is hit we emit a
// console warning (the File[] contract is preserved so the two callers —
// App.jsx window drop + FileImportView drop zone — need no signature change).
// If you change this, change DEFAULT_SCAN_DEPTH in file-explorer-intake.js in
// the same commit.
const DROP_WALK_MAX_DEPTH = 8;
let dropWalkDepthCapHit = false;

const walkEntry = (entry: FsEntry | null | undefined, depth = 0): Promise<File[]> => new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) {
        entry.file!((f) => resolve([f]), () => resolve([]));
    } else if (entry.isDirectory) {
        if (depth >= DROP_WALK_MAX_DEPTH) {
            // Match the picker walk: stop descending past the cap rather than
            // recursing without bound. Warn once per walk so a pathological
            // tree doesn't silently drop its deepest files.
            if (!dropWalkDepthCapHit) {
                dropWalkDepthCapHit = true;
                try {
                    // eslint-disable-next-line no-console
                    console.warn(`[drop-walk] Folder nested deeper than ${DROP_WALK_MAX_DEPTH} levels - files below this depth were skipped.`);
                } catch { /* console unavailable */ }
            }
            return resolve([]);
        }
        const reader = entry.createReader!();
        const all: File[] = [];
        const readBatch = () => {
            reader.readEntries(async (entries) => {
                if (!entries || entries.length === 0) return resolve(all);
                for (const ent of entries) {
                    const sub = await walkEntry(ent, depth + 1);
                    all.push(...sub);
                }
                readBatch(); // readEntries yields in batches; keep calling until empty
            }, () => resolve(all));
        };
        readBatch();
    } else {
        resolve([]);
    }
});

// Call this SYNCHRONOUSLY inside a drop event handler (before any await). The
// HTML5 spec invalidates DataTransfer.items immediately after the drop event's
// sync phase — webkitGetAsEntry() returns null after an await, so the walk must
// snapshot references up front and hand them off for async descent later.
export function snapshotDrop(dataTransfer: DataTransfer | null | undefined, { killSwitchKey = 'qm_disable_global_drop_walk' }: SnapshotDropOptions = {}): DropSnapshot {
    if (!dataTransfer) return { entries: [], fallbackFiles: [], killSwitch: false };
    const killSwitch = typeof window !== 'undefined' && !!(window as any)[killSwitchKey];
    const entries: FsEntry[] = [];
    const items = dataTransfer.items;
    if (!killSwitch && items && items.length > 0 && typeof items[0]!.webkitGetAsEntry === 'function') {
        for (let i = 0; i < items.length; i++) {
            const entry = items[i]!.webkitGetAsEntry?.() as FsEntry | null;
            if (entry) entries.push(entry);
        }
    }
    return { entries, fallbackFiles: Array.from(dataTransfer.files || []), killSwitch };
}

export async function collectFromSnapshot({ entries, fallbackFiles, killSwitch }: DropSnapshot): Promise<File[]> {
    if (killSwitch || entries.length === 0) return fallbackFiles;
    dropWalkDepthCapHit = false; // reset per-walk so a fresh drop warns again
    const collected: File[] = [];
    const promises = entries.map(entry => walkEntry(entry).then(fs => collected.push(...fs)));
    await Promise.all(promises);
    return collected.length > 0 ? collected : fallbackFiles;
}

// Legacy API kept so callers that can call sync-then-await in one function still
// work (they must NOT await anything between the drop event and this call).
export async function collectDroppedFiles(dataTransfer: DataTransfer | null | undefined, opts: SnapshotDropOptions = {}): Promise<File[]> {
    return collectFromSnapshot(snapshotDrop(dataTransfer, opts));
}
